import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
    BoardColumnDef,
    BoardLaneDef,
    BoardLock,
    BoardMove,
    BoardMoveBatch,
    BoardMoveCheck,
    BoardMovedItemGetter,
    BoardMoveVerdict,
    BoardPlacement,
    BoardPlaceRule,
} from "./Board.types";
import {
    announceLocked,
    announceSortedColumn,
    getBoardAnnouncement,
    getCellTitle,
    getColumnTitle,
    getKeyboardEdgeAnnouncement,
    PLACE_BY_HAND_INSTRUCTIONS,
    type BoardAnnouncementMessage,
    type BoardRefusalMessage,
} from "./boardAnnouncements";
import type { BoardCellEntry, BoardCells } from "./boardCells";
import {
    getAllPlacements,
    getBoardCellOrder,
    type BoardCollapse,
} from "./boardCollapse";
import {
    getBoardDragGroup,
    getGroupRefusals,
    type BoardDragGroup,
} from "./boardDragGroup";
import type { BoardDraggingState, BoardDragState } from "./boardDragState";
import type { BoardKeyboardDirection } from "./boardDropTargets";
import { locateBoardEntry } from "./boardMove";
import { getBoardRefusals } from "./boardMoveRules";
import { getCellDroppableId, getStripDroppableId } from "./boardSensors";
import { useBoardDragModel } from "./useBoardDragModel";

export interface UseBoardDragArgs<TItem> {
    cells: BoardCells<TItem>;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    collapse: BoardCollapse;
    getItemLabel: (item: TItem) => string;
    compareItems?: (first: TItem, second: TItem) => number;
    getMovedItem?: BoardMovedItemGetter<TItem>;
    canPlaceBetween: boolean;
    canPlaceAt?: BoardPlaceRule<TItem>;
    onMove: (move: BoardMove<TItem>) => void;
    /** Group drag is on when set. See `BoardProps.onMoveMany`. */
    onMoveMany?: (batch: BoardMoveBatch<TItem>) => void;
    selectedItemIds: ReadonlySet<string>;
    hasMultiValueLanes: boolean;
    getLock?: (item: TItem) => BoardLock | null;
    canMove?: (check: BoardMoveCheck<TItem>) => BoardMoveVerdict;
    /** The board's live region. */
    announceMessage: (message: string) => void;
}

export interface BoardRefusalAnnouncement {
    state: BoardDraggingState;
    reason: string;
    toMessage: BoardRefusalMessage;
}

/** A drag's announcement: `toGroupMessage` gets the card count for a group. */
interface BoardDragAnnouncement<TItem> {
    cells: BoardCells<TItem>;
    state: BoardDraggingState;
    toMessage: BoardAnnouncementMessage;
    toGroupMessage: (count: number) => string;
}

export interface LatestBoardDragArgs<TItem> extends UseBoardDragArgs<TItem> {
    /** Cell placements by droppable id, collapsed cells included. */
    cellPlacements: ReadonlyMap<string, BoardPlacement>;
    /** Collapsed column strips: column id by droppable id. */
    stripColumnIds: ReadonlyMap<string, string>;
    /** Every cell in board order, which a group keeps. */
    cellOrder: readonly BoardPlacement[];
}

/**
 * Drag state plus refs to it and to the latest props: dnd-kit events can
 * outrun renders, so handlers read refs instead of render-time values.
 */
export const useBoardDragStore = <TItem>(args: UseBoardDragArgs<TItem>) => {
    const [state, setState] = useState<BoardDragState<TItem>>({
        phase: "idle",
        focusKey: null,
        isKeyboard: false,
    });
    const { announceMessage: showAnnouncement } = args;
    const stateRef = useRef(state);
    const { columns, lanes } = args;
    const cellPlacements = useMemo(
        () =>
            new Map(
                getAllPlacements(columns, lanes).map((placement) => [
                    getCellDroppableId(placement),
                    placement,
                ]),
            ),
        [columns, lanes],
    );
    const stripColumnIds = useMemo(
        () => new Map(columns.map(({ id }) => [getStripDroppableId(id), id])),
        [columns],
    );
    const cellOrder = useMemo(
        () => getBoardCellOrder(columns, lanes),
        [columns, lanes],
    );
    const latest = { ...args, cellPlacements, stripColumnIds, cellOrder };
    const latestRef = useRef<LatestBoardDragArgs<TItem>>(latest);
    useLayoutEffect(() => {
        latestRef.current = latest;
    });
    const model = useBoardDragModel({ ...args, cellOrder });

    const commit = useCallback((next: BoardDragState<TItem>) => {
        stateRef.current = next;
        setState(next);
    }, []);

    const announce = useCallback(
        (
            cells: BoardCells<TItem>,
            placementKey: string,
            toMessage: BoardAnnouncementMessage,
        ) => {
            const latestArgs = latestRef.current;
            showAnnouncement(
                getBoardAnnouncement({
                    cells,
                    placementKey,
                    getItemLabel: latestArgs.getItemLabel,
                    columns: latestArgs.columns,
                    lanes: latestArgs.lanes,
                    toMessage,
                }),
            );
        },
        [showAnnouncement],
    );

    const announceDrag = useCallback(
        ({
            cells,
            state: { activeKey, group },
            toMessage,
            toGroupMessage,
        }: BoardDragAnnouncement<TItem>) =>
            group === null
                ? announce(cells, activeKey, toMessage)
                : showAnnouncement(toGroupMessage(group.itemIds.size)),
        [announce, showAnnouncement],
    );

    const announceRefusal = useCallback(
        ({
            state: { activeKey, target },
            reason,
            toMessage,
        }: BoardRefusalAnnouncement) => {
            const latestArgs = latestRef.current;
            const location = locateBoardEntry(latestArgs.cells, activeKey);
            showAnnouncement(
                location === null
                    ? ""
                    : toMessage({
                          itemLabel: latestArgs.getItemLabel(
                              location.entry.item,
                          ),
                          columnTitle: getCellTitle(
                              latestArgs,
                              target.placement,
                          ),
                          reason,
                      }),
            );
        },
        [showAnnouncement],
    );

    const announceKeyboardEdge = useCallback(
        (placement: BoardPlacement, direction: BoardKeyboardDirection) =>
            showAnnouncement(
                getKeyboardEdgeAnnouncement({
                    columns: latestRef.current.columns,
                    lanes: latestRef.current.lanes,
                    placement,
                    direction,
                }),
            ),
        [showAnnouncement],
    );

    const announceSortedCell = useCallback(
        (placement: BoardPlacement) => {
            const { columns: latestColumns, canPlaceBetween } =
                latestRef.current;
            const message = announceSortedColumn(
                getColumnTitle(latestColumns, placement.columnId),
            );
            showAnnouncement(
                canPlaceBetween
                    ? `${message} ${PLACE_BY_HAND_INSTRUCTIONS}`
                    : message,
            );
        },
        [showAnnouncement],
    );

    const announceLockedCard = useCallback(
        (item: TItem, { reason }: BoardLock) =>
            showAnnouncement(
                announceLocked(latestRef.current.getItemLabel(item), reason),
            ),
        [showAnnouncement],
    );

    const getGroup = useCallback((grabbed: BoardCellEntry<TItem>) => {
        const latestArgs = latestRef.current;
        return latestArgs.onMoveMany === undefined
            ? null
            : getBoardDragGroup({ ...latestArgs, grabbed });
    }, []);

    const getRefusals = useCallback(
        (grabbed: BoardCellEntry<TItem>, group: BoardDragGroup | null) => {
            const latestArgs = latestRef.current;
            return group === null
                ? getBoardRefusals({
                      item: grabbed.item,
                      from: grabbed.placement,
                      placements: Array.from(
                          latestArgs.cellPlacements.values(),
                      ),
                      canMove: latestArgs.canMove,
                  })
                : getGroupRefusals({
                      ...latestArgs,
                      group,
                      activeKey: grabbed.placementKey,
                  });
        },
        [],
    );

    return {
        state,
        stateRef,
        latestRef,
        model,
        commit,
        announce,
        announceDrag,
        announceRefusal,
        announceKeyboardEdge,
        announceSortedCell,
        announceLockedCard,
        getGroup,
        getRefusals,
    };
};
